import { ArrowDownIcon, EditIcon, JournalIcon, LogoutIcon, MenuIcon, UserIcon } from '@/icons';
import { Context } from '@/context/appContext';
import { AppStateActions } from '@/reducers/appReducer';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { PROFILE_ROUTES, ROUTES } from '@/constants/constants';
import { HeaderWrapper, LeftSection, Logo, Menu, ToolsSection } from './styles';
import { useIsMobile } from '@/hooks/useIsMobile';

const Header = () => {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.layout.header' });
  const {
    state: { document, processedDocument, authToken },
    dispatch,
  } = useContext(Context);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const theme = useTheme();
  const profileRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const variant = useMemo(() => (document && processedDocument ? 'editor' : 'homepage'), [document, processedDocument]);

  const handleOpenMenu = useCallback(() => setMenuOpen(true), []);
  const handleCloseMenu = useCallback(() => setMenuOpen(false), []);

  const handleLogoClick = () => dispatch({ type: AppStateActions.SET_DOCUMENT, payload: { document: null } });
  const handleSignInClick = () => dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: 'SIGN_IN' } });
  const handleSignUpClick = () => dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: 'SIGN_UP' } });

  const handleSignOutClick = useCallback(() => {
    handleCloseMenu();
    dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: undefined });
    navigate(ROUTES.home(i18n.language));
  }, [dispatch, handleCloseMenu, i18n.language, navigate]);

  const handleProfileClick = useCallback(
    (subRoute: PROFILE_ROUTES) => () => {
      handleCloseMenu();
      navigate(ROUTES.profile(i18n.language, subRoute));
    },
    [handleCloseMenu, i18n.language, navigate],
  );

  const handleFilesClick = useCallback(() => {
    navigate(ROUTES.myFiles(i18n.language));
  }, [i18n.language, navigate]);

  const handleCloseTools = useCallback(() => setToolsOpen(false), []);
  const handleToggleTools = useCallback(() => setToolsOpen((prev) => !prev), []);

  const handleNavigateTo = (path: string) => () => {
    navigate(path);
    handleCloseTools();
    setDrawerOpen(false);
  };

  const openDrawer = useCallback(() => {
    setToolsOpen(false);
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const toolsDropdown = (
    <Box>
      <Button
        onClick={handleToggleTools}
        sx={{
          px: 2.5,
          color: 'primary.main',
          fontSize: 16,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {t('allTools')}
        <ArrowDownIcon
          width={12}
          height={12}
          style={{
            transform: toolsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </Button>

      {toolsOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            backgroundColor: theme.designTokens.white,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 1000,
            display: 'flex',
            py: 10,
            px: { xs: 2, sm: 4, md: 39 },
            gap: { xs: 2, md: 20 },
          }}
        >
          <Box>
            <Typography component="p" sx={{ mb: 4, fontSize: 14, color: theme.designTokens.slate[500] }}>
              {t('edit')}
            </Typography>
            <Button
              onClick={handleNavigateTo(ROUTES.home(i18n.language))}
              sx={{ p: 0, gap: 2, color: 'primary.main', fontWeight: 400 }}
            >
              <EditIcon /> {t('editPdf')}
            </Button>
          </Box>

          <Box>
            <Typography component="p" sx={{ mb: 4, fontSize: 14, color: theme.designTokens.slate[500] }}>
              {t('convertFromPdf')}
            </Typography>
            <Button
              onClick={handleNavigateTo(ROUTES.convertToWord(i18n.language))}
              sx={{ p: 0, gap: 2, color: 'primary.main', fontWeight: 400 }}
            >
              <JournalIcon /> {t('pdfToWord')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  const mobileMenuContent = (
    <Box sx={{ width: 280, pt: 2, px: 2 }} role="presentation">
      <Box sx={{ mb: 3 }}>
        <Typography
          component="p"
          sx={{ mb: 2, fontSize: 12, color: theme.designTokens.slate[500], textTransform: 'uppercase' }}
        >
          {t('edit')}
        </Typography>
        <Button
          fullWidth
          onClick={handleNavigateTo(ROUTES.home(i18n.language))}
          sx={{
            justifyContent: 'flex-start',
            color: 'primary.main',
            fontWeight: 400,
            display: 'flex',
            gap: 1,
          }}
        >
          <EditIcon /> {t('editPdf')}
        </Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          component="p"
          sx={{ mb: 2, fontSize: 12, color: theme.designTokens.slate[500], textTransform: 'uppercase' }}
        >
          {t('convertFromPdf')}
        </Typography>
        <Button
          fullWidth
          onClick={handleNavigateTo(ROUTES.convertToWord(i18n.language))}
          sx={{
            justifyContent: 'flex-start',
            color: 'primary.main',
            fontWeight: 400,
            display: 'flex',
            gap: 1,
          }}
        >
          <JournalIcon /> {t('pdfToWord')}
        </Button>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {authToken ? (
          <>
            <Button
              onClick={() => {
                handleFilesClick();
                closeDrawer();
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              {t('myFiles')}
            </Button>
            <Button
              onClick={() => {
                closeDrawer();
                handleProfileClick(PROFILE_ROUTES.general)();
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              <Box sx={{ color: theme.designTokens.blueRibbon[600], mr: 1 }}>
                <UserIcon />
              </Box>
              {t('userMenu.general')}
            </Button>
            <Button
              onClick={() => {
                closeDrawer();
                handleSignOutClick();
              }}
              sx={{ justifyContent: 'flex-start', color: theme.designTokens.slate[700] }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <LogoutIcon width={20} height={20} />
              </ListItemIcon>
              {t('userMenu.logout')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant={variant === 'homepage' ? 'contained' : 'outlined'}
              onClick={() => {
                handleSignInClick();
                closeDrawer();
              }}
            >
              {t('logIn')}
            </Button>
            <Button
              variant={variant === 'homepage' ? 'contained' : 'outlined'}
              onClick={() => {
                handleSignUpClick();
                closeDrawer();
              }}
            >
              {t('signUp')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <HeaderWrapper variant={variant}>
      <LeftSection>
        <NavLink to={ROUTES.home(i18n.language)} onClick={handleLogoClick}>
          <Logo>
            <img src="/assets/images/logo.svg" alt={t('brand')} width="32" height="32" />
            {t('brand')}
          </Logo>
        </NavLink>
        <ToolsSection>{toolsDropdown}</ToolsSection>
      </LeftSection>

      {isMobile ? (
        <>
          <IconButton onClick={openDrawer} aria-label={t('allTools')} sx={{ color: theme.designTokens.slate[700] }}>
            <MenuIcon />
          </IconButton>
          <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
            {mobileMenuContent}
          </Drawer>
        </>
      ) : (
        <Menu>
          {authToken ? (
            <>
              <Button onClick={handleFilesClick}>{t('myFiles')}</Button>

              <IconButton
                onClick={handleOpenMenu}
                ref={profileRef}
                style={{ color: theme.designTokens.blueRibbon[600] }}
              >
                <UserIcon />
              </IconButton>
              <MuiMenu
                id="basic-menu"
                anchorEl={profileRef.current}
                PopoverClasses={{ paper: 'popupShadow' }}
                open={menuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{ sx: { minWidth: 45 } }}
              >
                <MuiMenuItem onClick={handleProfileClick(PROFILE_ROUTES.general)}>
                  <ListItemText>{t('userMenu.general')}</ListItemText>
                </MuiMenuItem>
                <MuiMenuItem onClick={handleProfileClick(PROFILE_ROUTES.cards)}>
                  <ListItemText>{t('userMenu.cards')}</ListItemText>
                </MuiMenuItem>
                <MuiMenuItem onClick={handleProfileClick(PROFILE_ROUTES.subscriptions)}>
                  <ListItemText>{t('userMenu.subscriptions')}</ListItemText>
                </MuiMenuItem>
                <MuiMenuItem onClick={handleProfileClick(PROFILE_ROUTES.payments)}>
                  <ListItemText>{t('userMenu.payments')}</ListItemText>
                </MuiMenuItem>
                <Divider sx={{ marginLeft: 4, marginRight: 4 }} />
                <MuiMenuItem onClick={handleSignOutClick}>
                  <ListItemIcon sx={(muiTheme) => ({ minWidth: muiTheme.spacing(8) })}>
                    <LogoutIcon width={20} height={20} />
                  </ListItemIcon>
                  <ListItemText>{t('userMenu.logout')}</ListItemText>
                </MuiMenuItem>
              </MuiMenu>
            </>
          ) : (
            <>
              <Button variant={variant === 'homepage' ? 'contained' : 'outlined'} onClick={handleSignInClick}>
                {t('logIn')}
              </Button>

              <Button variant={variant === 'homepage' ? 'contained' : 'outlined'} onClick={handleSignUpClick}>
                {t('signUp')}
              </Button>
            </>
          )}
        </Menu>
      )}
    </HeaderWrapper>
  );
};

export default Header;
